import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Chip, Tooltip, debounce } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HandshakeIcon from "@mui/icons-material/Handshake";
import { usePermissions } from "../../context/permissionContext";
import { validatePermission, canDeleteQueue } from "../../utils/permissionUtils";
import { getDealFlowColumns, FUNDING_STAGE_COLORS, normalizeFundingStageKey, formatFundingStageLabel } from "../../utils/dealFlowColumns";
import { CAMPAIGN_STATUSES, getConfigByRoute } from "../../utils/campaignStatusConfig";
import { deleteCampaign, restoreCampaign, permanentlyDeleteCampaign } from "../../utils/campaignViewActions";
import Loader from "../../components/Loader/Loader";
import PageShell from "../../components/layout/PageShell";
import PageHead from "../../components/layout/PageHead";
import useResponsiveColumns from "../../hooks/useResponsiveColumns";
import "../../components/common/css/datatable.scss";

// Responsive column visibility. Fields absent on a given route's column set are
// simply ignored (omitted = visible).
const CAMPAIGN_COL_PRIORITY = {
  id: "low",
  craax_account: "low",
  entitytype: "low",
  website: "low",
  city: "medium",
  camp_email: "medium",
  corporation_legal_name: "medium",
};

/**
 * Unified Campaign List Page
 *
 * Replaces all 11 per-status list components. Driven by campaignStatusConfig.
 * Accepts optional `statusKey` prop (used by ReviewHub tabs) or derives
 * the status from the current route via useLocation().
 */
const CampaignListPage = ({ statusKey: statusKeyProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions, permissionsMapping } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pageState, setPageState] = useState({
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
  });

  // Responsive column visibility — must be called before any early return below
  // (React rules of hooks). Fed to the DataGrid's columnVisibilityModel.
  const columnVisibilityModel = useResponsiveColumns(CAMPAIGN_COL_PRIORITY);

  // Resolve config: prop takes precedence, then route-based lookup
  let statusKey = statusKeyProp;
  let config = statusKey ? CAMPAIGN_STATUSES[statusKey] : null;
  if (!config) {
    const result = getConfigByRoute(location.pathname);
    if (result) {
      [statusKey, config] = result;
    }
  }

  const features = config?.features || {};
  const permissionPrefix = config?.permissionPrefix || "";

  // ---- Hooks (must be called unconditionally) ----
  const handleQuery = useCallback(
    debounce((e) => {
      setSearchTerm(e.target.value);
      setPageState((old) => ({ ...old, page: 1 }));
    }, 1000),
    []
  );

  useEffect(() => {
    if (!config) return;
    const token = localStorage.getItem("accessToken");
    setLoading(true);
    axios({
      method: "post",
      url: process.env.REACT_APP_API + config.apiEndpoint,
      headers: { "x-access-token": token },
      data: {
        ...config.apiParams,
        page: pageState.page,
        limit: pageState.pageSize,
        searchTerm,
      },
    })
      .then((response) => {
        setPageState((old) => ({
          ...old,
          data: response.data.data.rows,
          total: response.data.data.count,
        }));
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "An error occurred",
          timerProgressBar: true,
          icon: "error",
          confirmButtonText: "OK",
          showCloseButton: true,
        });
      });
  }, [pageState.page, pageState.pageSize, searchTerm, statusKey, refreshKey]);

  // Fallback — should never happen if routes are configured correctly
  if (!config) {
    return <div className="datatable"><p>Unknown campaign status page.</p></div>;
  }

  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  // ---- Action handlers ----
  const handleDelete = async (id) => {
    const result = await deleteCampaign(id);
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  const handleRestore = async (id) => {
    const result = await restoreCampaign(id);
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  const handlePermanentlyDelete = async (id) => {
    const result = await permanentlyDeleteCampaign(id);
    if (result.success && result.refresh) triggerRefresh();
  };

  // ---- Column definitions ----
  const buildColumns = () => {
    const cols = [
      { field: "id", headerName: "ID", width: 70 },
    ];

    // Active uses campaign_name instead of corporation_legal_name
    if (statusKey === "active") {
      cols.push({
        field: "campaign_name",
        headerName: "Campaign Name",
        flex: 1,
        minWidth: 170,
      });
      cols.push({
        field: "corporation_legal_name",
        headerName: "Corporate Name",
        flex: 1,
        minWidth: 170,
      });
    } else {
      cols.push({
        field: "corporation_legal_name",
        headerName: "Campaign Name",
        flex: 1,
        minWidth: 170,
      });
    }

    // Interest columns (Coming Soon)
    if (features.hasInterestColumns) {
      cols.push({
        field: "interest",
        headerName: "Interest",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const favorites = params.row.favorite_count || 0;
          const commitments = params.row.soft_commitment_count || 0;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip title={`${favorites} favorites`} arrow>
                <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                  <FavoriteIcon sx={{ fontSize: 16, color: "#e91e63" }} />
                  <span style={{ fontSize: "12px" }}>{favorites}</span>
                </div>
              </Tooltip>
              <Tooltip title={`${commitments} soft commitments`} arrow>
                <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                  <HandshakeIcon sx={{ fontSize: 16, color: "#1976d2" }} />
                  <span style={{ fontSize: "12px" }}>{commitments}</span>
                </div>
              </Tooltip>
            </div>
          );
        },
      });
      // Inline funding stage for Coming Soon
      cols.push({
        field: "funding_stage",
        headerName: "Stage",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          const stageKey = normalizeFundingStageKey(params.row.funding_stage);
          if (!stageKey) return <span style={{ color: "#999" }}>—</span>;
          const colors = FUNDING_STAGE_COLORS[stageKey] || { bg: "#f5f5f5", color: "#666" };
          return (
            <Chip
              label={formatFundingStageLabel(stageKey)}
              size="small"
              sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 500, fontSize: "11px" }}
            />
          );
        },
      });
    }

    // Standard columns
    cols.push(
      { field: "city", headerName: "Location", width: 170 },
      { field: "craax_account", headerName: "CRA Tax Account", width: 170 },
      { field: "entitytype", headerName: "Entity Type", width: 170 },
      { field: "website", headerName: "Website", width: 170 },
    );

    // Email column (Initial Approval, Coming Soon use it)
    if (features.hasEmailColumn) {
      cols.push({ field: "camp_email", headerName: "Email", width: 200 });
    }

    // Deal Flow columns (funding stage)
    if (features.hasDealFlowColumns) {
      cols.push(...getDealFlowColumns());
    }

    // Accredited column (Active only)
    if (features.hasAccreditedColumn) {
      cols.push({
        field: "is_acreadited",
        headerName: "Accredited",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <div className="cellAction">
            <span className={`status ${params.row.is_acreadited === 1 ? "Yes" : "No"}`}>
              {params.row.is_acreadited === 1 ? "Yes" : "No"}
            </span>
          </div>
        ),
      });
    }

    // Status badge column (For Review only — campaigns span multiple statuses)
    if (features.hasStatusColumn) {
      cols.push({
        field: "status_badge",
        headerName: "Status",
        flex: 1,
        minWidth: 200,
        // QA 2026-09-07 (F8): this queue spans INREVIEW (1) and HOLD (8); the
        // column used to print a constant.
        renderCell: (params) => {
          const st = Number(params.row?.campaign_status);
          const label = st === 8 ? "Paused - edits pending" : st === 1 ? "In review - edits pending" : "For Review";
          return (
            <div className="cellAction">
              <span className="status Pending">{label}</span>
            </div>
          );
        },
      });
    }

    // Type column
    if (features.hasTypeColumn) {
      cols.push({ field: "type", headerName: "Type", width: 170 });
    }

    // Action column
    cols.push({
      field: "action",
      headerName: "Action",
      width: features.hasRestore || features.hasPermanentDelete ? 250 : 200,
      renderCell: (params) => {
        const hasView = validatePermission(
          `${permissionPrefix}:View`,
          permissions,
          permissionsMapping
        );
        const hasDelete = features.hasDelete && validatePermission(
          `${permissionPrefix}:Delete`,
          permissions,
          permissionsMapping
        );
        const hasRestore = features.hasRestore && validatePermission(
          `${permissionPrefix}:Edit`,
          permissions,
          permissionsMapping
        );
        // QA 2026-09-07 (F23): the irreversible one was the only action with no permission check
        // (the Deleted queue's vocabulary defines :Edit but no :Delete, so resolve the grant)
        const hasPermanentDelete = features.hasPermanentDelete &&
          canDeleteQueue(permissionPrefix, permissions, permissionsMapping) !== false;
        return (
          <div className="cellAction">
            {hasView && (
              <Link to={`${config.route}/${params.row.id}`} style={{ textDecoration: "none" }}>
                <div className="viewButton">
                  <RemoveRedEyeOutlinedIcon />
                </div>
              </Link>
            )}
            {hasDelete && (
              <div className="deleteButton" onClick={() => handleDelete(params.row.id)}>
                <DeleteOutlineIcon />
              </div>
            )}
            {hasRestore && (
              <div className="viewButton" onClick={() => handleRestore(params.row.id)}>
                <RestoreIcon />
              </div>
            )}
            {hasPermanentDelete && (
              <div className="deleteButton" onClick={() => handlePermanentlyDelete(params.row.id)}>
                <DeleteForeverIcon />
              </div>
            )}
          </div>
        );
      },
    });

    return cols;
  };

  const columns = buildColumns();

  // ---- Render ----
  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title={`Campaigns · ${config.label}`}
        actions={
          <div className="tablesearch">
            <input type="text" placeholder="Search..." onChange={handleQuery} />
            <SearchOutlinedIcon className="sarchicon" />
          </div>
        }
      />
      <div className="datatable">
      <DataGrid
        className="datagrid"
        autoHeight
        rows={pageState.data}
        rowCount={pageState.total}
        rowsPerPageOptions={[10, 30, 50, 70, 100]}
        pagination
        page={pageState.page - 1}
        pageSize={pageState.pageSize}
        paginationMode="server"
        onPageChange={(newPage) => {
          setPageState((old) => ({ ...old, page: newPage + 1 }));
        }}
        onPageSizeChange={(newPageSize) =>
          setPageState((old) => ({ ...old, pageSize: newPageSize }))
        }
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
      />
      </div>
    </PageShell>
  );
};

export default CampaignListPage;
